/* eslint-disable react/no-danger */
import AspectRatioCover from '@comp/AspectRatioCover'
import SVGLocale from '@img/locale.svg'
import SVGLocalBlock from '@img/local-block.svg'
import { useTranslations } from 'use-intl'
import { useMemo } from 'react'
import { detail as agentDetail } from '@lib/route/agent'
import { propertyDetail } from '@lib/route/house'
import { clearHtmlTag, toSlug } from '@lib/utils'
import { forEach, isArray, omit, uniqBy, filter, last, isObject, isFunction, pick } from 'lodash-es'
import { urls } from '@lib/parse'
import { houseSearchType } from '@lib/config'
import { getGroupedData } from './filter-modal/lib'

/**
 * 去重地图范围
 */
export function uniqOptions(val) {
  return uniqBy(
    filter(val, (f) => f.type !== 'rect'), 
    (it) => it.value
  )
}

/**
 * 渲染中介
 */
function AgentOption({ option }) {
  return (
    <div className="flex-auto flex items-center gap-x-2">
      <div className="w-6">
        <AspectRatioCover
          subtile
          brand=""
          src={option.avatar}
          wrapperClassName="rounded-full"
          className="rounded-full object-cover"
          width={30}
          height={30}
          alt={option.label}
        />
      </div>
      <span dangerouslySetInnerHTML={{ __html: option.htmlLabel }} />
    </div>
  )
}

/**
 * 渲染 listing
 */
function ListingOption({ option }) {
  const ts = useTranslations('search')
  const th = useTranslations('house')
  const listingDesc = useMemo(() => ({
    listing: { iconColor: 'text-primary', text: ts('listingForSale') },
    rental: { iconColor: 'text-warning', text: ts('rental') },
    nolisting: { iconColor: 'text-meta', text: th('nolisting') },
  }), [ts, th])

  const { houseId, typeId } = option
  const descName = houseId
    ? typeId !== 5 ? 'listing' : 'rental'
    : 'nolisting'

  const desc = listingDesc[descName]

  return (
    <div className="flex-auto">
      <div
        className="font-medium"
        dangerouslySetInnerHTML={{ __html: option.htmlLabel }} 
      />
      {desc && (
        <div className="text-f.6 bg-cf8 py-0.5 px-2 inline-flex items-center gap-0.5 leading-none">
          <SVGLocale width=".5rem" className={desc.iconColor} />
          {desc.text}
        </div>
      )}
    </div>
  )
}

export function DefaultOptionMeta({ label, dot = true, suffix = '' }) {
  if(!label) {
    return null
  }

  return (
    <>
      {dot && <span className="inline-block h-1 w-1 bg-cd8 rounded-full" />}
      <span>
        <span dangerouslySetInnerHTML={{ __html: label }} />
        {suffix}
      </span>
    </>
  )
}

/**
 * 渲染默认项
 */
function DefaultOption({ option }) {
  const isSchool = option.type === 'school'
  const ts = useTranslations('search')
  
  return (
    <div className="flex-auto flex items-center gap-2">
      <div className="shrink-0">
        <SVGLocalBlock />
      </div>
      <div>
        <div
          className="font-medium line-clamp-1"
          dangerouslySetInnerHTML={{ __html: option.htmlLabel }} 
        />
        <div className="text-helper text-f.6 flex items-center gap-x-1">
          <DefaultOptionMeta label={option.locationLabel} dot={false} />
          {isSchool && (
            <>
              <DefaultOptionMeta label={option.district_name} />
              <DefaultOptionMeta label={option.school_type_label} />
              <DefaultOptionMeta label={option.year_range} suffix={ts('level')} />
              <DefaultOptionMeta label={option.authority} />
            </>
          )}
        </div>
      </div>
    </div>
  )
}

function OptionItem({ option }) {
  if(option.type === 'agent') {
    return (
      <AgentOption option={option} />
    )
  }

  if(option.type === 'listing') {
    return (
      <ListingOption option={option} />
    )
  }

  return (
    <DefaultOption option={option} />
  )
}

/**
 * 渲染搜索选项
 */
export function renderOption(props, option) {
  return (
    <div 
      {...omit(props, ['key'])} 
      key={`${option.type}-${option.value}`}
    >
      <div className="flex items-center justify-between w-full">
        <OptionItem option={option} />
        {/* {option.count !== undefined && (
          <span className="font-medium ml-2">
            {option.count}
          </span>
        )} */}
      </div>
    </div>
  )
}

/**
 * 处理搜索列表数据
 */
export function getFilterOptions(data, ts, ta) {
  const result = []
  const optionKeys = ['locations', 'schools', 'streets', 'listings', 'agents']
  const locationLabels = {
    region: ts('district'),
    district: ta('district'),
    suburb: ta('suburb'),
    school: ts('schools'),
    street: ts('streets'),
  }
  
  forEach(optionKeys, (key) => {
    if(isArray(data?.[key]) && data[key].length > 0) {
      forEach(data[key], (item) => {
        const tmp = {
          value: item.id,
          count: item.houseCount, // 房源数量
        }

        if(key === 'agents') {
          Object.assign(tmp, {
            typeName: ts('agent'),
            label: item.name,
            htmlLabel: item.name,
            avatar: item.avatar,
            slug: item.slug || toSlug(item.name),
            type: 'agent'
          })
        } else {
          const label = clearHtmlTag(item.label)
          Object.assign(tmp, {
            typeName: ts(key),
            label,
            slug: item.slug || toSlug(label),
            htmlLabel: item.label,
            lat: parseFloat(item.lat),
            lng: parseFloat(item.lng),
            type: item.level,
            locationLabel: locationLabels[item.level]
          })
          
          if(key === 'listings') {
            tmp.type = 'listing'
            tmp.houseId = item.houseId
            tmp.typeId = item.typeId
          } else if(key === 'schools') {
            Object.assign(tmp, pick(item, [
              'gender', 'school_type_label', 'authority', 
              'district_name', 'year_range'
            ]))
          }
        }
      
        result.push(tmp)
      })
    }
  })

  return result
}

/**
 * 处理搜索结果
 * - 学校，街道，直接跳转到搜索列表
 * - 中介, 房源直接跳转到详情
 */
export function handleSearchItemClick(props, callback) {
  const { data, reason, type, setter, pushTrack, locale } = props
  const tmp = [...data]
  const singleType = ['school', 'street']
  const lastItem = last(tmp)
  const isToListings = singleType.indexOf(lastItem?.type) > -1
  const isToAgent = lastItem?.type === 'agent'
  const isToListing = lastItem?.type === 'listing'
  const isSelect = reason === 'selectOption'
  
  if(isSelect && (isToListings || isToAgent || isToListing)) {
    let url = ''
    let targetData = null
    
    // 跳转到列表
    if(isToListings) {
      targetData = getGroupedData([lastItem])
      url = urls.listing(type, targetData, locale, true)
    
    // 跳转到中介详情
    } else if(isToAgent) {
      targetData = { agent: lastItem }
      url = agentDetail(lastItem.slug, locale)

    // 跳转到房源详情
    } else if(isToListing) {
      targetData = { listing: lastItem }
      url = propertyDetail(lastItem.slug, locale)

      const isHouse = [houseSearchType.sold, houseSearchType.estimate].indexOf(type) === -1

      if(isHouse && isFunction(pushTrack)) {
        pushTrack('autocomplate', { 
          houseId: lastItem?.value, 
          suburbId: lastItem.suburbId || 0 
        })
      }
    }
    
    if(targetData) {
      tmp.pop()
      setter(tmp)
      isFunction(callback) && callback(tmp)
      
      targetData = isToAgent ? null : {
        type, 
        searchParam: targetData, 
      }
    }

    return { url, targetData, isToListings }
  }
  
  // 如果是新添加选项，需要去掉学校、街道这些单选项
  const inputValue = isSelect 
    ? filter(data, (f) => singleType.indexOf(f.type) === -1) 
    : data
  
  setter(inputValue)
  isFunction(callback) && callback(inputValue)
  return null
}

/**
 * 生成从url过来的默认搜索回填数据
 * 
 * @param {Object} data 是 parse.js 中 parse函数返回的值
 * @returns {Object}
 */
export function getSearchDefaultValue(
  data, 
  propKeys = ['region', 'district', 'suburb', 'school', 'street']
) {
  const tmp = []
  
  isObject(data) && forEach(propKeys, (type) => {
    const val = data[type]
    const isArr = isArray(val)
    const isValidate = isArr || isObject(val)

    if(!isValidate) return
    
    forEach(isArr ? val : [val], (v) => tmp.push({ 
      ...v, 
      label: String(v.label).replace('-', ' '),
      slug: v.slug || v.label, 
      type 
    }))
  })
  
  return tmp
}
